import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { Drawer, IconButton } from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil";
import {
    backgroundColorState,
    darkerBackgroundColorState,
    greyState,
    lighterBackgroundColorState,
    planPickerDrawerOpenState,
    subscriptionOfferingsState,
    textColorState,
    themeState,
} from "@/lib/store";
import useHandleAppUpgrade from "src/hooks/useHandleAppUpgrade";

const PlanPickerDrawer: React.FC = () => {
    const [open, setOpen] = useRecoilState(planPickerDrawerOpenState);
    const { handleAppUpgrade } = useHandleAppUpgrade();

    const offerings = useRecoilValue(subscriptionOfferingsState) as any[] | null;
    const textColor = useRecoilValue(textColorState);
    const theme = useRecoilValue(themeState);
    const backgroundColor = useRecoilValue(backgroundColorState);
    const darkerBackgroundColor = useRecoilValue(darkerBackgroundColorState);
    const lighterBackgroundColor = useRecoilValue(lighterBackgroundColorState);
    const grey = useRecoilValue(greyState);

    const packageTypeOrder: Record<string, number> = { WEEKLY: 0, MONTHLY: 1, ANNUAL: 2 };
    const validPackages = React.useMemo(
        () =>
            (offerings ?? [])
                .filter((pkg: any) => !pkg.product?.title?.toLowerCase().includes("basic") && !pkg.identifier?.toLowerCase().includes("basic"))
                .sort((a: any, b: any) => (packageTypeOrder[a.packageType] ?? 99) - (packageTypeOrder[b.packageType] ?? 99)),
        [offerings]
    );

    const [selectedPackage, setSelectedPackage] = React.useState<any>(() => validPackages[0] ?? null);

    React.useEffect(() => {
        if (open) setSelectedPackage(validPackages[0] ?? null);
    }, [open]);

    React.useEffect(() => {
        if (validPackages.length && !selectedPackage) setSelectedPackage(validPackages[0]);
    }, [validPackages]);

    const getPlanLabel = (pkg: any) => {
        const type = pkg.packageType;
        if (type === "ANNUAL") return "Annual";
        if (type === "MONTHLY") return "Monthly";
        if (type === "WEEKLY") return "Weekly";
        return pkg.product?.title ?? pkg.identifier;
    };

    const handleContinue = () => {
        setOpen(false);
        handleAppUpgrade(selectedPackage);
    };

    return (
        <Drawer
            variant="temporary"
            ModalProps={{ keepMounted: false }}
            anchor="bottom"
            PaperProps={{
                sx: {
                    borderRadius: "16px 16px 0 0",
                    paddingTop: "env(safe-area-inset-top)",
                    paddingBottom: "env(safe-area-inset-bottom)",
                    padding: 3,
                },
            }}
            open={open}
        >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textColor }}>Choose your plan</Typography>
                <IconButton onClick={() => setOpen(false)} sx={{ color: textColor }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center", mb: 3 }}>
                {validPackages.map((pkg) => {
                    const isSelected = selectedPackage?.identifier === pkg.identifier;
                    const label = getPlanLabel(pkg);
                    const price = pkg.product?.priceString ?? "";
                    return (
                        <Box
                            key={pkg.identifier}
                            onClick={() => setSelectedPackage(pkg)}
                            sx={{
                                flex: 1,
                                maxWidth: "120px",
                                border: isSelected ? "2px solid #31aba6" : `2px solid ${theme === "light" ? lighterBackgroundColor : backgroundColor}`,
                                borderRadius: "12px",
                                padding: "10px 8px",
                                textAlign: "center",
                                cursor: "pointer",
                                backgroundColor: isSelected ? darkerBackgroundColor : "transparent",
                                "&:hover": { backgroundColor: darkerBackgroundColor, borderColor: !isSelected && darkerBackgroundColor },
                                transition: "all 0.15s ease",
                            }}
                        >
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: textColor, lineHeight: 1.2 }}>{label}</Typography>
                            <Typography sx={{ fontSize: "11px", fontWeight: 400, color: grey, lineHeight: 1.2 }}>Advanced</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: textColor, marginTop: "4px" }}>{price}</Typography>
                        </Box>
                    );
                })}
            </Box>
            <Button
                variant="contained"
                size="large"
                disabled={!selectedPackage}
                onClick={handleContinue}
                sx={{
                    borderRadius: "30px",
                    textTransform: "initial",
                    width: "100%",
                    backgroundColor: "#000",
                    color: "#fff",
                    "&:hover": { backgroundColor: "#000" },
                    "&:disabled": { backgroundColor: "#000" },
                }}
            >
                Continue
            </Button>
        </Drawer>
    );
};

export default PlanPickerDrawer;
